<template>
    <div :class="classes">
        <Drawer :transfer="false" :model-value="true" width="90" @onClose="close">
            <hox-loading-layer v-if="isLoadingDeliverable" :is-full-screen="false" />
            <template #header>
                <div>
                    <div v-if="drawerHeaderIsVisible" class="hox-drawer-header">
                        <ButtonGroup
                            class="deliverable-drawer-nav hox-drawer-nav"
                            :class="{ 'deliverable-drawer-nav--bulk': isBulk }"
                        >
                            <Button
                                v-if="!isBulk"
                                class="deliverable-drawer-nav-item deliverable-drawer-nav-item--preview hox-drawer-item hox-drawer-item--preview"
                                :type="activeTab === LibraryDrawerTab.Preview ? 'primary' : 'default'"
                                @click="onTabChange(LibraryDrawerTab.Preview)"
                            >
                                Preview
                            </Button>
                            <Button
                                class="deliverable-drawer-nav-item deliverable-drawer-nav-item--qa hox-drawer-nav-item hox-drawer-nav-item--qa"
                                :type="activeTab === LibraryDrawerTab.QA ? 'primary' : 'default'"
                                @click="onTabChange(LibraryDrawerTab.QA)"
                            >
                                Quality Assurance
                            </Button>
                        </ButtonGroup>

                        <div v-if="headerDeliverable" class="hox-drawer-header-info">
                            <h3 class="hox-drawer-header-info__label" :title="deliverableReportingLabel">
                                <div class="hox-drawer-header-info__label-text">
                                    {{ deliverableReportingLabel }}
                                </div>
                            </h3>
                            <qa-status-label :status="deliverableStatus" />
                        </div>
                    </div>
                </div>
            </template>

            <template #close>
                <div class="hox-drawer__close-icon-container" data-testid="qa-drawer__close">
                    <Icon size="44" type="ios-close" @click="close" />
                </div>
            </template>

            <div v-if="!isLoadingDeliverable" class="hox-drawer__container">
                <div class="hox-drawer__body">
                    <div
                        v-if="hasErrorLoadingDeliverable || deliverableDoesNotExist"
                        class="hox-drawer__error-message-wrapper"
                    >
                        <hox-alert v-if="deliverableDoesNotExist" type="info">
                            <template #title>You do not have access to this ad</template>
                            <template #content>
                                <p>
                                    Check with your administrator that the ad you’re looking for is still live or
                                    request access.
                                </p>
                            </template>
                        </hox-alert>
                        <hox-alert v-else type="danger">
                            <template #title>Uh oh!</template>
                            <template #content>
                                <p>Sorry! We could not get your ads this time. Try again a moment.</p>
                            </template>
                            <template #actionItems>
                                <Button type="primary" @click="getDeliverable">Retry</Button>
                            </template>
                        </hox-alert>
                    </div>

                    <deliverable-preview-drawer-tab
                        v-else-if="activeTab === 'preview'"
                        :deliverable="internalPreviewDeliverable"
                    />

                    <QaDrawerTab
                        v-else-if="activeTab === 'qa'"
                        :all-selected-mode="isFilterBulk"
                        :deliverables="internalQaDeliverables"
                        @selectedDeliverableUpdate="onSelectedDeliverableUpdate"
                        @statusHasBeenUpdated="$emit('statusHasBeenUpdated')"
                        @userStatusHasBeenUpdated="$emit('userStatusHasBeenUpdated')"
                        @annotationsHasBeenUpdated="$emit('annotationsHasBeenUpdated')"
                    />
                </div>
            </div>
        </Drawer>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapStores } from "pinia";

import getPaginatedDeliverables from "@/services/deliverables/getPaginatedDeliverables";
import URLManager from "@/services/URLManager";
import { DeliverableLibraryAction, DeliverableLibraryGetters } from "@/store/modules/deliverableLibrary";
import QaStatusLabel from "@/components/Qa/QaStatusLabel.vue";
import DeliverablePreviewDrawerTab from "@/components/Campaign/DeliverablePreviewDrawerTab.vue";
import QaDrawerTab from "@/components/Qa/DrawerTab/QaDrawerTab.vue";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { LibraryDrawerTab } from "@/enums/library";

const QueryParams = {
    ActiveTab: "dd-at",
    EditableGroupValueIds: "dd-gv",
    MastertTemplateId: "dd-mt",
    Language: "dd-lng",
    BulkFilterQa: "dd-bfqa"
};

export default defineComponent({
    components: {
        QaDrawerTab,
        DeliverablePreviewDrawerTab,
        QaStatusLabel
    },
    props: {
        deliverables: {
            type: [Array, null],
            required: true
        }
    },

    emits: ["statusHasBeenUpdated", "userStatusHasBeenUpdated", "annotationsHasBeenUpdated", "closed"],

    setup() {
        const ga4 = useGA4Tracking();
        return {
            ga4
        };
    },

    data() {
        return {
            deliverableDoesNotExist: false,
            fetchedDeliverables: undefined,
            hasErrorLoadingDeliverable: false,
            isLoadingDeliverable: false,
            selectedDeliverable: null,
            LibraryDrawerTab
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        activeTab: {
            get() {
                return this.$store.state.deliverableLibrary.drawer.activeTab;
            },
            set(value) {
                this.$store.dispatch(DeliverableLibraryAction.SetDrawerActiveTab, value);
            }
        },

        campaignId() {
            return this.$route.params.campaignId;
        },

        classes() {
            const classes = ["hox-drawer"];
            /*
            As we can't simply hide/show the header slot, we are going to
            control the headers visibility using CSS.
            */
            if (!this.drawerHeaderIsVisible) {
                classes.push("hox-drawer--hide-header");
            }

            return classes;
        },

        deliverableStatus() {
            return this.$store.getters[DeliverableLibraryGetters.computedDeliverableStatus](
                this.headerDeliverable.idHash
            );
        },

        deliverableReportingLabel() {
            return this.headerDeliverable.reportingLabel;
        },

        drawerHeaderIsVisible() {
            return !this.isLoadingDeliverable && !this.hasErrorLoadingDeliverable && !this.deliverableDoesNotExist;
        },

        headerDeliverable() {
            if (this.isFilterBulk) {
                return this.selectedDeliverable;
            }

            return this.selectedDeliverable || this.internalDeliverables[0];
        },

        internalDeliverables() {
            return this.deliverables.length ? this.deliverables : this.fetchedDeliverables;
        },

        internalPreviewDeliverable() {
            return this.internalDeliverables[0];
        },

        internalQaDeliverables() {
            if (this.isFilterBulk) {
                // We just need to pass the filters, the child component will figure out the content based on filters.
                return [];
            }

            return this.internalDeliverables;
        },

        isArrayBulk() {
            return Array.isArray(this.deliverables) && this.deliverables.length > 1;
        },

        isBulk() {
            return this.isArrayBulk || this.isFilterBulk;
        },

        isFilterBulk() {
            return (
                (this.creativeLibraryFiltersStore.allSelectedMode && !this.isSingleDeliverable) ||
                !!this.$route.query[QueryParams.BulkFilterQa]
            );
        },

        isSingleDeliverable() {
            return Array.isArray(this.deliverables) && this.deliverables.length === 1;
        },

        allFiltersAreActive() {
            const editableGroupsFilterOptions = this.$store.state.deliverableLibrary.filterOptions.editableGroups;

            const allGroupsHaveValues = !editableGroupsFilterOptions.some(groupFilterOption => {
                return !this.creativeLibraryFiltersStore.selectedEditableIdsByGroup[groupFilterOption.name];
            });

            const selectedLanguages = this.creativeLibraryFiltersStore.selectedLanguages;
            const selectedSizes = this.creativeLibraryFiltersStore.selectedSizes;

            return allGroupsHaveValues && selectedLanguages.length > 0 && selectedSizes.length > 0;
        }
    },

    watch: {
        activeTab() {
            this.setActiveTabQueryParams();
        }
    },

    mounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.DELIVERABLES,
            featureContext: GA4.FEATURE_CONTEXTS.REVIEW_DELIVERABLES
        });
    },

    beforeUnmount() {
        this.unsetAllQueryParams();
    },

    unmounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
            featureCategory: GA4.FEATURE_CATEGORIES.DELIVERABLES,
            featureContext: GA4.FEATURE_CONTEXTS.CLOSE_REVIEW_DELIVERABLES
        });
    },

    created() {
        this.urlManager = new URLManager(this.$router, {
            defaults: [
                {
                    key: QueryParams.ActiveTab,
                    value: [LibraryDrawerTab.QA]
                }
            ],
            tracked: Object.values(QueryParams)
        });

        if (this.deliverables && this.deliverables.length) {
            if (this.isSingleDeliverable) {
                this.setDeliverableQueryParams();
            }
            // TODO: add query param settings for the bulk QA
        } else if (!this.isBulk) {
            this.getDeliverableFromQueryParams();
        }

        if (!this.activeTab) {
            this.activeTab = this.$route.query[QueryParams.ActiveTab] || LibraryDrawerTab.QA;
        }

        this.setActiveTabQueryParams();
        this.setBulkFilterQaQueryParam();
    },

    methods: {
        onTabChange(tab: (typeof LibraryDrawerTab)[keyof typeof LibraryDrawerTab]) {
            if (this.activeTab !== tab) {
                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                    interactionText:
                        tab === LibraryDrawerTab.Preview
                            ? GA4.CLICK_INTERACTION_TEXTS.PREVIEW_TAB
                            : GA4.CLICK_INTERACTION_TEXTS.QUALITY_ASSURANCE_TAB
                });
                this.activeTab = tab;
            }
        },

        close() {
            this.$emit("closed");
        },

        async getDeliverableFromQueryParams() {
            this.hasErrorLoadingDeliverable = false;
            this.isLoadingDeliverable = true;

            if (!this.allFiltersAreActive) {
                this.deliverableDoesNotExist = true;
                this.isLoadingDeliverable = false;
                this.close();
                return;
            }

            try {
                const { deliverables } = await getPaginatedDeliverables({
                    campaignId: this.campaignId,
                    libraryFilters: this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                    page: 1,
                    deliverablesPerPage: 1,
                    store: this.$store
                });

                const [deliverable] = deliverables;
                if (!deliverable) {
                    this.deliverableDoesNotExist = true;
                    this.isLoadingDeliverable = false;
                    return;
                }
                this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, [deliverable]);
                this.fetchedDeliverables = [deliverable];
            } catch {
                this.hasErrorLoadingDeliverable = true;
            }
            this.isLoadingDeliverable = false;
        },

        onSelectedDeliverableUpdate(deliverable) {
            if (deliverable) {
                this.selectedDeliverable = deliverable;
            }
        },

        setActiveTabQueryParams() {
            this.urlManager.setURLParams(
                [
                    {
                        key: QueryParams.ActiveTab,
                        value: [this.activeTab]
                    }
                ],
                true
            );
        },

        setBulkFilterQaQueryParam() {
            this.urlManager.setURLParams(
                [
                    {
                        key: QueryParams.BulkFilterQa,
                        value: this.isFilterBulk ? [1] : []
                    }
                ],
                true
            );
        },

        setDeliverableQueryParams() {
            this.urlManager.setURLParams(
                [
                    {
                        key: QueryParams.EditableGroupValueIds,
                        value: this.internalDeliverables[0].editableGroupValueIds
                    },
                    {
                        key: QueryParams.MastertTemplateId,
                        value: [this.internalDeliverables[0].masterTemplate._id]
                    },
                    {
                        key: QueryParams.Language,
                        value: [this.internalDeliverables[0].language]
                    }
                ],
                true
            );
        },

        unsetAllQueryParams() {
            this.urlManager.setURLParams(
                Object.values(QueryParams).map(key => ({
                    key,
                    value: []
                })),
                true
            );
        }
    }
});
</script>
